import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UserService } from "@/services/users/UserService";
import { RunType } from "@/types/agent/brand-integrity";
import { getPhoneCheckKind, PHONE_CHECK_LABELS, PHONE_TESTS_PATH } from "@/utils/agent/phoneCheck";

interface BrandTestStartedPayload {
  testRunId: string;
  agentName: string;
  userId: string;
  runType?: string;
}

interface BrandTestProgressPayload {
  testRunId: string;
  progress: number;
  total: number;
  userId: string;
}

interface BrandTestCompletedPayload {
  testRunId: string;
  status: string;
  userId: string;
  agentName?: string;
  runType?: RunType;
  overallScore?: number;
}

interface AuditGenerationStartedPayload {
  testRunId: number;
  userId: string;
  agentId: string;
  agentName: string;
  totalCategories: number;
  targetScenarios: number;
}

interface AuditGenerationProgressPayload {
  testRunId: number;
  userId: string;
  category: string;
  categoryLabel: string;
  scenarioCount: number;
}

interface AuditGenerationCacheHitPayload {
  testRunId: number;
  userId: string;
  scenarioCount: number;
}

interface TestFailedPayload {
  testRunId: number | string;
  error: string;
  userId?: string;
}

export interface AuditNotificationCallbacks {
  onGenerationStarted?: (payload: AuditGenerationStartedPayload) => void;
  onGenerationProgress?: (payload: AuditGenerationProgressPayload) => void;
  onGenerationCacheHit?: (payload: AuditGenerationCacheHitPayload) => void;
  onGenerationComplete?: (payload: { testRunId: number; scenarioCount: number }) => void;
  onAuditFailed?: (payload: TestFailedPayload) => void;
}

export const useBrandIntegrityNotifications = (
  onProgress?: (testRunId: string, progress: number, total: number) => void,
  onCompleted?: (testRunId: string, payload?: BrandTestCompletedPayload) => void,
  onStarted?: (testRunId: string, agentName: string, runType?: string) => void,
  auditCallbacks?: AuditNotificationCallbacks,
) => {
  const queryClient = useQueryClient();
  const callbacksRef = useRef({ onProgress, onCompleted, onStarted, auditCallbacks });

  // Update the ref whenever callbacks change
  useEffect(() => {
    callbacksRef.current = { onProgress, onCompleted, onStarted, auditCallbacks };
  }, [onProgress, onCompleted, onStarted, auditCallbacks]);

  useEffect(() => {
    let isMounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupNotificationListener = async () => {
      try {
        const {
          data: { user },
          error: userError,
        } = await UserService.getUser();

        if (!isMounted) return;

        if (userError || !user) {
          console.warn("Unable to get current user for brand test notifications:", userError);
          return;
        }

        const userId = user.id || null;

        if (!userId) {
          console.warn("No user ID available for brand test notifications");
          return;
        }

        channel = supabase.channel("brand-tests");

        channel
          .on("broadcast", { event: "test-started" }, ({ payload }: { payload: BrandTestStartedPayload }) => {
            if (isMounted && payload.userId === userId) {
              callbacksRef.current.onStarted?.(payload.testRunId, payload.agentName, payload.runType);
            }
          })
          .on("broadcast", { event: "test-progress" }, ({ payload }: { payload: BrandTestProgressPayload }) => {
            if (isMounted && payload.userId === userId) {
              callbacksRef.current.onProgress?.(payload.testRunId, payload.progress, payload.total);

              // Invalidate queries to show new results as they arrive
              queryClient.invalidateQueries({
                queryKey: ["brand-test-results", payload.testRunId],
              });
              queryClient.invalidateQueries({
                queryKey: ["brand-test-run-details", payload.testRunId],
              });
            }
          })
          .on("broadcast", { event: "test-completed" }, ({ payload }: { payload: BrandTestCompletedPayload }) => {
            if (isMounted && payload.userId === userId) {
              const isAudit = payload.runType === "full_audit";
              const phoneCheckKind = getPhoneCheckKind(payload.runType);
              const label = isAudit
                ? "Brand Audit complete"
                : phoneCheckKind
                  ? `${PHONE_CHECK_LABELS[phoneCheckKind]} complete`
                  : "Genie Trust Center test complete";
              const description = isAudit && typeof payload.overallScore === "number"
                ? `Score: ${payload.overallScore}/100${payload.agentName ? ` · ${payload.agentName}` : ""}`
                : `Click to view results${payload.agentName ? ` for "${payload.agentName}"` : ""}`;
              toast.success(label, {
                description,
                duration: 6000,
                action: {
                  label: "View",
                  onClick: () => {
                    window.location.href = phoneCheckKind ? PHONE_TESTS_PATH : "/genie-hub/brand-integrity";
                  },
                },
              });

              queryClient.invalidateQueries({ queryKey: ["brand-test-runs"] });
              queryClient.invalidateQueries({ queryKey: ["brand-test-runs-infinite"] });

              callbacksRef.current.onCompleted?.(payload.testRunId, payload);
            }
          })
          .on("broadcast", { event: "audit-generation-started" }, ({ payload }: { payload: AuditGenerationStartedPayload }) => {
            if (isMounted && payload.userId === userId) {
              callbacksRef.current.auditCallbacks?.onGenerationStarted?.(payload);
            }
          })
          .on("broadcast", { event: "audit-generation-progress" }, ({ payload }: { payload: AuditGenerationProgressPayload }) => {
            if (isMounted && payload.userId === userId) {
              callbacksRef.current.auditCallbacks?.onGenerationProgress?.(payload);
            }
          })
          .on("broadcast", { event: "audit-generation-cache-hit" }, ({ payload }: { payload: AuditGenerationCacheHitPayload }) => {
            if (isMounted && payload.userId === userId) {
              callbacksRef.current.auditCallbacks?.onGenerationCacheHit?.(payload);
            }
          })
          .on(
            "broadcast",
            { event: "audit-generation-complete" },
            ({ payload }: { payload: { testRunId: number; scenarioCount: number; userId: string } }) => {
              if (isMounted && payload.userId === userId) {
                callbacksRef.current.auditCallbacks?.onGenerationComplete?.({
                  testRunId: payload.testRunId,
                  scenarioCount: payload.scenarioCount,
                });
              }
            },
          )
          .on("broadcast", { event: "test-failed" }, ({ payload }: { payload: TestFailedPayload }) => {
            if (isMounted) {
              callbacksRef.current.auditCallbacks?.onAuditFailed?.(payload);
              queryClient.invalidateQueries({ queryKey: ["brand-test-runs-infinite"] });
            }
          })
          .on("broadcast", { event: "audit-failed" }, ({ payload }: { payload: TestFailedPayload }) => {
            if (isMounted) {
              toast.error("Brand Audit failed", {
                description: payload.error || "The audit could not complete.",
                duration: 6000,
              });
              callbacksRef.current.auditCallbacks?.onAuditFailed?.(payload);
              queryClient.invalidateQueries({ queryKey: ["brand-test-runs-infinite"] });
            }
          })
          .subscribe((status) => {
            if (status === "SUBSCRIBED") {
              console.log("Subscribed to brand-tests channel");
            } else if (status === "CLOSED") {
              console.log("Unsubscribed from brand-tests channel");
            }
          });
      } catch (error) {
        console.error("Error setting up brand test notification listener:", error);
      }
    };

    setupNotificationListener();

    return () => {
      isMounted = false;
      if (channel) {
        channel.unsubscribe();
      }
    };
  }, [queryClient]);
};
